import React, { useEffect } from 'react';

import { Spinner } from 'react-bootstrap';
import { store } from 'redux/store';
import { setCurrentTab } from 'redux/reducers/meteorologyFieldsLayoutReducer';
import Instructions from 'components/Meteorology/Fields/Comparator/Instructions';
import Toolbar from 'components/Meteorology/Fields/Comparator/Toolbar';
import Display from 'components/Meteorology/Fields/Display';
import ComparatorMobile from 'components/Meteorology/Fields/Comparator/Mobile';
import buildImageDisplayData from 'helper/products/comparator/buildImageDisplayData';
import useComparatorControlPanel from 'utils/hooks/meteorology/comparator/useComparatorControlPanel';
import useComparatorParams from 'utils/hooks/meteorology/comparator/useComparatorParams';
import useComparatorPeriods from 'utils/hooks/meteorology/comparator/useComparatorPeriods';
import useImages from 'utils/hooks/meteorology/comparator/useImages';
import useImagesDownload from 'utils/hooks/meteorology/comparator/useImagesDownload';
import useImageRowSize from 'utils/hooks/meteorology/useImageRowSize';
import useIsMobile from 'utils/hooks/useIsMobile';
import QueryErrorState from 'components/DesignSystem/QueryErrorState';

export default function Comparator() {
  const {
    baseModel,
    confrontationalModel,
    baseForecastDateDiff,
    confrontationalForecastDateDiff,
    baseRuntime,
    confrontationalRuntime,
    baseMember,
    confrontationalMember,
    period,
  } = useComparatorControlPanel();
  const fillableFields = [
    baseModel, confrontationalModel, baseForecastDateDiff, confrontationalForecastDateDiff,
    baseRuntime, confrontationalRuntime, baseMember, confrontationalMember, period,
  ];
  const showInstructions = fillableFields.every((v) => v === undefined);
  const comparatorParams = useComparatorParams();
  const {
    periods,
    isFetching: periodsFetching,
    queryError: periodsError,
  } = useComparatorPeriods(comparatorParams);
  const {
    images,
    isLoading: imagesLoading,
    isFetching: imagesFetching,
    triggerIsActive,
    queryError: imagesError,
  } = useImages(comparatorParams, periods);
  const queryError = periodsError ?? imagesError;
  const {
    imageSets,
    columnLabels,
    rowLabels,
  } = buildImageDisplayData(images);
  const {
    onDownloadClick,
    downloadDisabled,
  } = useImagesDownload(images, !triggerIsActive);
  const { setRowSize } = useImageRowSize();
  const isMobile = useIsMobile();

  useEffect(() => {
    setRowSize(3);
    store.dispatch(setCurrentTab('comparator'));
  }, [setRowSize]);

  if (isMobile) {
    return (
      <ComparatorMobile
        showInstructions={showInstructions}
        imageSets={imageSets}
        columnLabels={columnLabels}
        rowLabels={rowLabels}
        onDownloadClick={onDownloadClick}
        downloadDisabled={downloadDisabled}
        isLoading={periodsFetching || imagesLoading}
        isFetching={!imagesLoading && imagesFetching}
        queryError={queryError}
      />
    );
  }

  const spinner = (
    <div className="pb-3 text-center">
      <Spinner />
    </div>
  );

  function renderDisplay() {
    if (queryError) {
      return (
        <QueryErrorState
          title={queryError.data}
          message={queryError.description}
        />
      );
    }
    if (periodsFetching || imagesLoading) return spinner;
    return (
      <>
        <Display
          imageSets={imageSets}
          columnLabels={columnLabels}
          rowLabels={rowLabels}
          fetchingImages={false}
        />
        {(!imagesLoading && imagesFetching) && spinner}
      </>
    );
  }

  return (
    <div>
      { showInstructions && <Instructions />}
      {!showInstructions && (
        <>
          <Toolbar onDownloadClick={onDownloadClick} downloadDisabled={downloadDisabled} />
          <div className="mt-3">
            {renderDisplay()}
          </div>
        </>
      )}
    </div>
  );
}
